import React, { useState, useEffect, useRef } from 'react';

/**
 * Custom React hook to toggle a debug state when the 'D' key is pressed 
 * twice rapidly (within 500 milliseconds).
 *
 * @param {boolean} initialDebugState - The initial state of the debug flag.
 * @returns {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} 
 * An array containing the current debug state and a setter function.
 */
const useDebugDoubleD = (initialDebugState = false) => {
  const [isDebugEnabled, setIsDebugEnabled] = useState(initialDebugState);
  // useRef is used to store the timestamp of the last 'D' press
  // It won't cause a re-render when it changes.
  const lastPressTime = useRef(0);
  const pressTimeoutMs = 500; // Time window for a double press (in milliseconds)

  useEffect(() => {
    // 1. Define the handler function for keydown events
    const handleKeyDown = (event) => {
      // Check if the pressed key is 'D' or 'd'
      if (event.key === 'D' || event.key === 'd') {
        const currentTime = Date.now();
        const timeSinceLastPress = currentTime - lastPressTime.current;

        // Check if the time since the last 'D' press is within the limit
        if (timeSinceLastPress > 0 && timeSinceLastPress < pressTimeoutMs) {
          // Double 'D' press detected!
        //   console.log('Double D press detected! Toggling debug flag.');
          
          // Prevent default behavior (though 'D' usually has none)
          event.preventDefault(); 

          // 2. Toggle the debug state using the functional update form
          setIsDebugEnabled(prev => !prev);
          
          // Reset the last press time to prevent a triple-press trigger 
          // right after the toggle.
          lastPressTime.current = 0; 

        } else {
          // First 'D' press, record the time
          lastPressTime.current = currentTime;
        }
      } else {
        // If any other key is pressed, reset the state of the sequence
        // This ensures the double 'D' must be consecutive.
        lastPressTime.current = 0;
      }
    };

    // 3. Attach the event listener to the entire document
    document.addEventListener('keydown', handleKeyDown);

    // 4. Cleanup function: remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [pressTimeoutMs]); // The dependency array is empty because the logic only relies on event listeners and refs/setters.

  return [isDebugEnabled, setIsDebugEnabled];
};

export default useDebugDoubleD;